import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/api/client'
import { filesApi, type FileRecord, type BlockNoteJsonContent } from './filesApi'

export const fileKeys = {
  all: (workspaceSlug: string) => ['files', workspaceSlug] as const,
  list: (workspaceSlug: string, params?: Record<string, unknown>) =>
    ['files', workspaceSlug, 'list', params ?? {}] as const,
  content: (workspaceSlug: string, fileId: string) =>
    ['files', workspaceSlug, fileId, 'content'] as const,
}

export function useFiles(
  workspaceSlug: string,
  params?: { parentId?: string | null; projectId?: string | null; includeArchived?: boolean },
) {
  const { fetchWithAuth } = useApiClient()

  return useQuery({
    queryKey: fileKeys.list(workspaceSlug, params ?? {}),
    queryFn: () => filesApi.list(fetchWithAuth, workspaceSlug, params),
    enabled: Boolean(workspaceSlug),
  })
}

export function useFileMutations(workspaceSlug: string) {
  const { fetchWithAuth } = useApiClient()
  const queryClient = useQueryClient()

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: fileKeys.all(workspaceSlug) })

  const createFolder = useMutation({
    mutationFn: (data: { name: string; parentId?: string | null; projectId?: string | null }) =>
      filesApi.createFolder(fetchWithAuth, workspaceSlug, data),
    onSuccess: invalidate,
  })

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      filesApi.update(fetchWithAuth, workspaceSlug, id, { name }),
    onSuccess: invalidate,
  })

  const archive = useMutation({
    mutationFn: (id: string) => filesApi.archive(fetchWithAuth, workspaceSlug, id),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => filesApi.remove(fetchWithAuth, workspaceSlug, id),
    onSuccess: invalidate,
  })

  const createDoc = useMutation({
    mutationFn: (data: { name: string; parentId?: string | null; projectId?: string | null; content?: BlockNoteJsonContent }) =>
      filesApi.createDoc(fetchWithAuth, workspaceSlug, data),
    onSuccess: invalidate,
  })

  return { createFolder, createDoc, rename, archive, remove, invalidate }
}

export function useDownloadUrl(workspaceSlug: string, fileId: string | null) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: ['files', workspaceSlug, fileId, 'download-url'],
    queryFn: () => filesApi.getDownloadUrl(fetchWithAuth, workspaceSlug, fileId!),
    enabled: Boolean(workspaceSlug && fileId),
    staleTime: 1000 * 60 * 4, // URLs expire, re-fetch before 5min
  })
}

export function usePreviewUrl(workspaceSlug: string, fileId: string | null) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: ['files', workspaceSlug, fileId, 'preview-url'],
    queryFn: () => filesApi.getPreviewUrl(fetchWithAuth, workspaceSlug, fileId!),
    enabled: Boolean(workspaceSlug && fileId),
    staleTime: 1000 * 60 * 4,
  })
}

type BrowserFileWithPath = File & { webkitRelativePath?: string }

function normalizeRelativePath(file: File): string {
  const candidate = (file as BrowserFileWithPath).webkitRelativePath?.trim() ?? ''
  if (!candidate || !candidate.includes('/')) return ''
  return candidate.replace(/^\/+/, '').replace(/\/+$/, '')
}

export interface UploadFileState {
  name: string
  progress: number
  done: boolean
  error?: string
}

export function useFileUpload(workspaceSlug: string, parentId?: string | null, projectId?: string | null) {
  const { fetchWithAuth } = useApiClient()
  const queryClient = useQueryClient()

  const uploadFiles = async (
    files: FileList | File[],
    onProgress?: (states: UploadFileState[]) => void,
  ): Promise<FileRecord[]> => {
    const fileArray = Array.from(files)
    if (!fileArray.length) return []

    const states: UploadFileState[] = fileArray.map((f) => ({ name: f.name, progress: 0, done: false }))
    onProgress?.(states)

    const completed: FileRecord[] = []
    const folderCache = new Map<string, string | null>()
    folderCache.set('', parentId ?? null)

    const ensureFolder = async (folderPath: string): Promise<string | null> => {
      if (!folderPath) return parentId ?? null
      const normalized = folderPath.replace(/^\/+/, '').replace(/\/+$/, '')
      if (!normalized) return parentId ?? null
      if (folderCache.has(normalized)) return folderCache.get(normalized)!

      const parts = normalized.split('/').filter(Boolean)
      let currentPath = ''
      let currentParent = parentId ?? null

      for (const segment of parts) {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment
        const existing = folderCache.get(currentPath)
        if (existing !== undefined) { currentParent = existing; continue }
        const created = await filesApi.createFolder(fetchWithAuth, workspaceSlug, {
          name: segment,
          parentId: currentParent,
        })
        currentParent = created.id
        folderCache.set(currentPath, created.id)
      }
      return currentParent
    }

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i]
      try {
        const relativePath = normalizeRelativePath(file)
        const pathParts = relativePath ? relativePath.split('/').filter(Boolean) : []
        const fileName = pathParts.length ? pathParts[pathParts.length - 1] : file.name
        const folderPath = pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : ''
        const targetParentId = await ensureFolder(folderPath)

        states[i] = { ...states[i], progress: 15 }
        onProgress?.([...states])

        const { file: record, uploadUrl } = await filesApi.requestUpload(fetchWithAuth, workspaceSlug, {
          name: fileName,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          parentId: targetParentId,
          projectId: projectId ?? null,
        })

        states[i] = { ...states[i], progress: 45 }
        onProgress?.([...states])

        let completedRecord: FileRecord
        try {
          const res = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
          })
          if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`)
          completedRecord = await filesApi.completeUpload(fetchWithAuth, workspaceSlug, record.id)
        } catch {
          completedRecord = await filesApi.uploadBinary(fetchWithAuth, workspaceSlug, record.id, file)
        }

        states[i] = { ...states[i], progress: 100, done: true }
        onProgress?.([...states])
        completed.push(completedRecord)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Ошибка загрузки'
        states[i] = { ...states[i], progress: 0, done: false, error: msg }
        onProgress?.([...states])
      }
    }

    await queryClient.invalidateQueries({ queryKey: fileKeys.all(workspaceSlug) })
    return completed
  }

  return { uploadFiles }
}

export function useDocContent(workspaceSlug: string, fileId: string | null) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: fileKeys.content(workspaceSlug, fileId ?? ''),
    queryFn: () => filesApi.getContent(fetchWithAuth, workspaceSlug, fileId!),
    enabled: Boolean(workspaceSlug && fileId),
    staleTime: 1000 * 30,
  })
}

export function useSaveDocContent(workspaceSlug: string) {
  const { fetchWithAuth } = useApiClient()
  return useMutation({
    mutationFn: ({ fileId, content }: { fileId: string; content: BlockNoteJsonContent }) =>
      filesApi.saveContent(fetchWithAuth, workspaceSlug, fileId, content),
  })
}
